import { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Form,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Strong,
} from '@customerio/pluma-components/react';
import { useSignupFlow } from '../signup-flow';
import { dataCenterSubmittedProps, SignupAnalyticsEvent, signupTypeProps } from '../utils/analytics';
import {
  DATA_CENTER_DESCRIPTION,
  DATA_CENTER_HEADING,
  DATA_CENTER_PERMANENCE_NOTE,
  DataCenterOptionCards,
} from './data-center-option-cards';

/**
 * The `signup_skip_onboarding_questions` variant of the onboarding step: only
 * the data center is asked, since the post-auth wizard covers the product
 * questions. It is the last journeys step, so its submit runs the register call.
 */
export function DataCenterStep() {
  const {
    formValues,
    patchFormValues,
    effectiveSignupType,
    resolvedSignupType,
    isCompletingSignup,
    goToNextStep,
    trackSignupEvent,
  } = useSignupFlow();

  // Fire once per step entry; the ref keeps StrictMode's double-invoked
  // effects from double-counting the funnel.
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    trackSignupEvent(SignupAnalyticsEvent.DataCenterViewed, signupTypeProps({ signupType: effectiveSignupType }));
  }, [trackSignupEvent, effectiveSignupType]);

  // "Unset" for `dataCenter` is the empty string, never null — see the note on
  // `SignupFormValues`.
  const isSubmitDisabled = formValues.dataCenter === '';

  const onSubmit = async () => {
    trackSignupEvent(
      SignupAnalyticsEvent.DataCenterSubmitted,
      dataCenterSubmittedProps({
        signupType: effectiveSignupType,
        dataCenter: formValues.dataCenter,
        resolvedSignupType,
      }),
    );
    await goToNextStep();
  };

  return (
    <Box data-testid="signup-step-data-center">
      <PanelHeader>
        <PanelTitle level="1">{DATA_CENTER_HEADING}</PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Form onSubmit={onSubmit}>
          <Paragraph variant="secondary" color="base" mb="200">
            {DATA_CENTER_DESCRIPTION} <Strong>{DATA_CENTER_PERMANENCE_NOTE}</Strong>
          </Paragraph>

          <DataCenterOptionCards
            value={formValues.dataCenter}
            onChange={(value) => patchFormValues({ dataCenter: value })}
            isHorizontal
          />

          <Flex justifyContent="flex-end" mt="300">
            <Button
              type="submit"
              variant="primary"
              icon="chevron-right"
              iconPosition="trailing"
              isDisabled={isSubmitDisabled}
              isLoading={isCompletingSignup}
            >
              Continue
            </Button>
          </Flex>
        </Form>
      </PanelSection>
    </Box>
  );
}
